import React, { useEffect, useId, useRef } from 'react';
import CgFormLabel from './CgFormLabel';
import CgErrorLabel from './CgErrorLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { cn } from 'modules/Common/utils/cn';

// appearance-none box with the checkbox-white-tick tick image (main.css) on a
// primary background.
//
// `align-middle`, not `align-top`: a 16px box on a 24px line box sits 4px proud
// of the text it labels if it aligns to the line-box top. The recipe this
// replaced carried Bootstrap's `vertical-align: top` without its compensating
// `margin-top: .25em`, so the two drifted apart.
const BOX =
  'h-4 w-4 rounded checkbox-white-tick appearance-none border border-black/25 bg-white ' +
  'bg-contain bg-center bg-no-repeat align-middle cursor-pointer ' +
  'checked:bg-primaryColor checked:border-primaryColor disabled:cursor-not-allowed';

// The standalone box's extra states (R50): the dash of a partial selection, a
// visibly unavailable box, and a keyboard focus ring.
const STANDALONE_BOX =
  'm-0 shrink-0 indeterminate:bg-primaryColor indeterminate:border-primaryColor ' +
  'disabled:bg-slate-100 disabled:opacity-40 focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-primaryColor/40 focus-visible:ring-offset-1';

/**
 * The checkbox outside a form config: a controlled box with a third state
 * ("some selected", set on the element as the DOM only allows) and, when it
 * can't be ticked, the reason why — as a tooltip and to a screen reader.
 * `reasonTitle={false}` leaves the native tooltip off, for a caller that shows the
 * reason in the design system's tooltip around the box instead.
 */
function StandaloneCheckbox({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  title,
  reason,
  reasonTitle = true,
  className,
  ...rest
}) {
  const ref = useRef(null);
  const reasonId = useId();
  const partial = !!indeterminate && !checked;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial;
  }, [partial]);
  return (
    // The tooltip sits on the wrapper: a disabled input gets no pointer events.
    <span
      className={cn('relative inline-flex align-middle', className)}
      title={(reasonTitle ? reason : null) || title}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e)}
        aria-describedby={reason ? reasonId : undefined}
        className={cn(BOX, STANDALONE_BOX)}
        {...rest}
      />
      {reason ? (
        <span
          id={reasonId}
          className="sr-only"
        >
          {reason}
        </span>
      ) : null}
    </span>
  );
}

/** The checkbox of a form config (`formControl`); without one, the standalone box. */
const CgCheckbox = ({
  formControl,
  error,
  loading,
  onChange,
  hideLabel = false,
  ...standalone
}) => {
  if (!formControl) {
    return (
      <StandaloneCheckbox
        onChange={onChange}
        {...standalone}
      />
    );
  }

  const getLabel = () => {
    return <CgFormLabel formControl={formControl} />;
  };

  const getControl = () => {
    let commonProps = {
      id: formControl.name,
      key: `formControl-${formControl.key}-key`,
      type: 'checkbox',
      checked: formControl.value,
      name: formControl.name,
      onChange: (e) => {
        onChange({
          target: { name: formControl.name, value: !formControl.value },
        });
      },
      disabled: formControl.disabled || loading,
      className: BOX,
    };

    // If loading, show spinner overlay on checkbox
    if (loading) {
      return (
        <div className="relative inline-block">
          <input
            {...commonProps}
            style={{ opacity: 0.5 }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-blue-500 text-xs"
              style={{ fontSize: '8px' }}
            />
          </div>
        </div>
      );
    }

    return <input {...commonProps} />;
  };

  if (hideLabel) {
    return getControl();
  }

  return (
    <div
      className={`mt-4 ${formControl.className}`}
      key={formControl.key}
    >
      <div className="flex items-center">
        {getControl()}
        <label
          htmlFor={formControl.name}
          className="ml-2"
        >
          {getLabel()}
        </label>
      </div>
      <CgErrorLabel error={error} />
    </div>
  );
};

export default CgCheckbox;
